import React, { useState } from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import CTAButton from "./CTAButton";
import { useAuth } from "../../auth/AuthContext";

/**
 * Item 9 rewrite (2026-08-20) — replaces the old $15/dev/mo single-plan
 * model with Free/Pro, matching what's actually live in the backend.
 * Only real, shippable-today claims: history-aware routing and team
 * analytics are Pro-only because that's what routing-engine.mjs and
 * get-analytics actually enforce, not aspirational copy.
 *
 * Every price here is a NUMBER, and becomes a string only at render
 * time. An earlier revision kept `price: "$99"` as a display string next to
 * a separate `monthlyTotal: 99 * 12`, which hardcoded 99 twice — change the
 * monthly price and the headline updates while the discount badge silently
 * keeps computing against the old base, with no test failure. That is the
 * same derived-number drift this card was fixed to stop, so there is exactly
 * one authoritative figure per interval and nothing restates it.
 */
const TIERS = {
  free: {
    name: "Free",
    monthlyAmount: 0,
    unit: "/month",
    features: [
      "1 connected repo",
      "Up to 5 team members",
      "Workload-based reviewer routing",
      "Complexity scoring on every PR",
    ],
  },
  pro: {
    name: "Pro",
    monthlyAmount: 99,
    // Read off the live Stripe price object (price_1U6htd…), NOT derived.
    // The old code computed the annual figure as 99 × 12 × 0.8 = $950.40,
    // which is not what the account actually charges — the real annual
    // price is $948. The displayed "/month" figure happened to match
    // ($948 ÷ 12 = $79 exactly) so nothing looked wrong, but the page was
    // inventing a total rather than reporting one.
    //
    // ⚠️ Pre-launch: live-mode prices are created fresh (CLAUDE.md's
    // Stripe checklist step 1). Re-read the live price and update this
    // number, or the card advertises a total nobody will be charged.
    annualTotal: 948,
    unit: "/month",
    features: [
      "Unlimited repos",
      "Unlimited team members",
      "History-aware reviewer routing",
      "Full team analytics",
    ],
  },
};

function TierCard({ tier, highlighted }) {
  const { isAuthenticated } = useAuth();
  const [billingInterval, setBillingInterval] = useState("monthly");
  const config = TIERS[tier];
  const isPro = tier === "pro";

  // Everything annual derives from config.annualTotal, and the monthly base
  // it is compared against derives from config.monthlyAmount — the two
  // authoritative figures, neither restated anywhere. Math.round on the
  // discount errs in the honest direction: the real saving is 20.2%, so
  // "20% off" understates rather than overpromises.
  const showAnnual = isPro && billingInterval === "annual";
  const annualMonthly = isPro ? Math.round(config.annualTotal / 12) : null;
  const annualPct = isPro
    ? Math.round((1 - config.annualTotal / (config.monthlyAmount * 12)) * 100)
    : null;

  const cta = isPro
    ? isAuthenticated
      // "Upgrade →" asserted the visitor is NOT already on Pro, which this
      // card cannot know: plan state lives behind getMyTeam(), and marketing
      // pages deliberately make zero API calls (CloudFront access logs are
      // the only funnel visibility for anonymous traffic — see CLAUDE.md).
      // Fetching plan data here to fix a label would forfeit that. /team is
      // already the plan-aware surface: TeamPage gates its own "Upgrade to
      // Pro →" on plan.reason being free/trial, so an existing Pro customer
      // correctly sees billing management instead. This label just has to
      // stop claiming something it can't check.
      ? <CTAButton to="/team" className="w-full">Manage your plan →</CTAButton>
      : <CTAButton to="/signup" className="w-full">Start free trial</CTAButton>
    : isAuthenticated
      ? <CTAButton to="/dashboard" variant="secondary" className="w-full">Go to dashboard →</CTAButton>
      : <CTAButton to="/signup" variant="secondary" className="w-full">Start free</CTAButton>;

  return (
    <div
      className={`w-full max-w-md rounded-2xl border p-8 ${
        highlighted ? "border-rf-accent/30 bg-rf-bg-secondary" : "border-rf-border bg-rf-bg-secondary/60"
      }`}
    >
      <div className="mb-1 text-sm font-semibold text-rf-text-secondary">{config.name}</div>

      <div className="mb-2 flex items-baseline gap-2 font-mono">
        <span
          className="text-[clamp(2.5rem,5vw+1rem,4.5rem)] font-bold leading-none text-rf-text-primary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {showAnnual ? `$${annualMonthly}` : `$${config.monthlyAmount}`}
        </span>
        <span className="text-sm text-rf-text-secondary">{config.unit}</span>
      </div>

      {/*
        The annual plan charges $948 up front but the headline figure reads
        "$79 /month", which on its own invites a visitor to believe they're
        paying $79 month-to-month. Naming the real charge is the whole point
        of this line — a price shown per-month and billed per-year has to say
        so on the same screen as the number.

        Rendered for every tier/interval rather than only the annual case, so
        toggling doesn't shift the card's layout and the Free card lines up
        with Pro's.

        aria-live matters here specifically: this line is the disclosure, and
        without it a screen reader user who toggles to Annual hears nothing
        change — the headline still reads "$79 /month" and the fact that it
        bills yearly is announced to nobody. A disclosure that only sighted
        users receive isn't one. It's a static region on the Free card, which
        never updates, so it stays silent there.
      */}
      <p className="mb-2 text-xs text-rf-text-secondary" aria-live="polite">
        {!isPro
          ? "No credit card required"
          : showAnnual
            ? `$${config.annualTotal} billed once a year`
            : "Billed monthly"}
      </p>

      {isPro && (
        // The selected interval was conveyed by background colour alone, so
        // it did not exist for anyone not looking at it. aria-pressed makes
        // the state real; the group gives the pair a name so the buttons
        // aren't announced as two unrelated controls.
        <div className="mb-6 flex items-center gap-2" role="group" aria-label="Billing interval">
          <button
            type="button"
            onClick={() => setBillingInterval("monthly")}
            aria-pressed={billingInterval === "monthly"}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              billingInterval === "monthly" ? "bg-rf-accent text-rf-bg-primary" : "text-rf-text-secondary hover:text-rf-text-primary"
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setBillingInterval("annual")}
            aria-pressed={billingInterval === "annual"}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              billingInterval === "annual" ? "bg-rf-accent text-rf-bg-primary" : "text-rf-text-secondary hover:text-rf-text-primary"
            }`}
          >
            Annual — {annualPct}% off
          </button>
        </div>
      )}
      {!isPro && <div className="mb-6" />}

      <ul className="mb-8 flex flex-col gap-3">
        {config.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2">
            <CheckCircleIcon className="h-5 w-5 shrink-0 text-rf-accent" aria-hidden="true" />
            <span className="text-sm text-rf-text-primary">{feature}</span>
          </li>
        ))}
      </ul>

      {cta}

      {isPro && !isAuthenticated && (
        <p className="mt-3 text-center text-xs text-rf-text-secondary">
          14-day trial, full Pro access. No credit card required to start.
        </p>
      )}
    </div>
  );
}

export default function PriceCard({ tier = "pro" }) {
  return <TierCard tier={tier} highlighted={tier === "pro"} />;
}

export { TIERS };
